import React, { useState } from 'react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { GenericTable, type GenericTableColumn } from '@/styles/GenericTable';
import { GenericTag, type StatusType } from '@/styles/GenericTag';
import { NewItemButton, SearchButton } from '@/styles/StandardButtons';
import { useEntityList } from '@/hooks/useEntityList';
import { useCompanyLookups } from '@/hooks/useCompanyLookups';
import { useOpenEntityRecord } from '@/hooks/useOpenEntity';
import { usePermissions } from '@/contexts/PermissionsContext';
import { useUser } from '@/contexts/UserContext';
import { useProject } from '@/components/ProjectSelector';
import { fetchEntityAttachments } from '@/lib/entity-attachments';
import { BidForm } from '../components/bid-form';
import { bidApiService } from '../services/bid-api-service';
import { BidPackageForm } from './BidPackageForm';
import { bidPackageApi, PACKAGE_STATUS_LABEL, type BidPackageRecord, type BidPackageStatus, type PackageInvitation } from './api';

const STATUS_TAG: Record<BidPackageStatus, StatusType> = { draft: 'draft', open: 'open', closed: 'completed', cancelled: 'cancelled' };

const formatDate = (iso: string | null | undefined) => (iso ? new Date(`${iso}T00:00:00`).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '-');

/**
 * The Bid Packages list: the generic server-driven table + the generic form, like every other module. A vendor that was invited sees the same list (only the packages it
 * is invited to) and opens the same form read-only, with Create Bid.
 */
export function BidPackagesPage() {
  const { companyId, userRole } = useUser();
  const { selectedProject } = useProject();
  const { can } = usePermissions();
  const vendorView = userRole === 'vendor';
  const list = useEntityList<BidPackageRecord, BidPackageRecord>({
    entityType: 'bid_package',
    projectId: selectedProject,
    enabled: !!companyId,
    mode: 'pages',
    defaultSort: { columnKey: 'package_id', direction: 'desc', sortKey: 'package_id' },
    mapRow: (row) => row,
  });
  const { projects, users } = useCompanyLookups(!!companyId, { includeInactiveProjects: true });
  const vendors = vendorView ? [] : users.filter((u) => u.role === 'vendor');
  const [open, setOpen] = useState<{ pkg: BidPackageRecord | null } | null>(null);
  const [bidFor, setBidFor] = useState<{ pkg: BidPackageRecord; invitation: PackageInvitation } | null>(null);
  const [openBid, setOpenBid] = useState<{ bid: any; attachments: any[]; pkg: BidPackageRecord | null } | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openPackage = (pkg: BidPackageRecord) => setOpen({ pkg });
  useOpenEntityRecord('bid_package', (id: string) => bidPackageApi.get(id), openPackage);

  const loadBid = async (bidId: string) => {
    const [bid, attachments] = await Promise.all([bidApiService.getBid(bidId), fetchEntityAttachments('bid', bidId, { ascending: false })]);
    const pkg = bid.bid_package_id ? await bidPackageApi.get(bid.bid_package_id).catch(() => null) : null;
    setOpenBid({ bid, attachments, pkg });
  };

  const handleDelete = async (pkg: BidPackageRecord) => {
    if (!window.confirm(`Delete "${pkg.title}"?`)) return;
    try { await bidPackageApi.remove(pkg.id); list.reload(); } catch (e) { setError(e instanceof Error ? e.message : 'Failed to delete the bid package'); }
  };

  const columns: GenericTableColumn<BidPackageRecord>[] = [
    { key: 'package_id', header: 'Package ID', width: '6.5rem', sortable: true, sortKey: 'package_id' },
    { key: 'title', header: 'Title', sortable: true, sortKey: 'title' },
    { key: 'project', header: 'Project', sortable: true, sortKey: 'project', render: (row) => row.project?.name || '-' },
    { key: 'due_date', header: 'Due Date', width: '7rem', sortable: true, sortKey: 'due_date', render: (row) => formatDate(row.due_date) },
    { key: 'status', header: 'Status', width: '6.5rem', sortable: true, sortKey: 'status', render: (row) => <GenericTag type="status" value={STATUS_TAG[row.status]}>{PACKAGE_STATUS_LABEL[row.status]}</GenericTag> },
    ...(!vendorView && can('bids.delete')
      ? [{ key: 'actions', header: '', width: '5rem', render: (row: BidPackageRecord) => (
        <button type="button" className="text-xs text-red-600 hover:underline" onClick={(e) => { e.stopPropagation(); void handleDelete(row); }}>Delete</button>
      ) } as GenericTableColumn<BidPackageRecord>]
      : []),
  ];

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-end gap-2">
        {!vendorView && can('bids.create') && <NewItemButton label="New" onClick={() => setOpen({ pkg: null })} />}
        <SearchButton onSearch={list.setSearchInput} placeholder="Search" />
      </div>
      {(list.error || error) && (
        <Alert variant="destructive"><AlertDescription>{list.error || error}</AlertDescription></Alert>
      )}
      <GenericTable
        {...list.tableProps}
        columns={columns}
        selectable={false}
        onRowClick={openPackage}
        rowKey={(row) => row.id}
        minTableWidth={800}
        emptyState={<span className="text-sm text-gray-500">{vendorView ? 'You have no bid packages yet' : 'No bid packages yet'}</span>}
      />

      {open && (
        <BidPackageForm
          key={open.pkg?.id || 'new'}
          pkg={open.pkg}
          projects={projects}
          vendors={vendors}
          vendorView={vendorView}
          onClose={() => setOpen(null)}
          onSaved={list.reload}
          onCreateBid={(pkg, invitation) => { setOpen(null); setBidFor({ pkg, invitation }); }}
          onOpenBid={(bidId) => { setOpen(null); void loadBid(bidId); }}
        />
      )}

      {bidFor && (
        <BidForm
          isOpen
          bidPackage={{ id: bidFor.pkg.id, title: bidFor.pkg.title, project_id: bidFor.pkg.project_id, vendor_id: bidFor.invitation.vendor_id, vendor_name: bidFor.invitation.vendor_name, status: bidFor.pkg.status }}
          onClose={() => setBidFor(null)}
          onSubmit={() => { setBidFor(null); list.reload(); }}
        />
      )}
      {openBid && (
        <BidForm
          isOpen
          bid={openBid.bid}
          attachments={openBid.attachments}
          bidPackage={{ id: openBid.bid.bid_package_id, title: openBid.pkg?.title || 'Bid package', project_id: openBid.bid.project_id, vendor_id: openBid.bid.vendor_id, vendor_name: openBid.bid.subcontractor_name || '', status: openBid.pkg?.status }}
          onClose={() => setOpenBid(null)}
          onSubmit={() => { setOpenBid(null); list.reload(); }}
        />
      )}
    </div>
  );
}
